// @flow

import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { Link } from "react-router-dom";
import { push } from "connected-react-router";
import classNames from "classnames";
import { TooltipDropdown, View } from "@components";
import { route } from "@routes/helper";
import type { State } from "@store/reducers";
import {
  withPermissions,
  type PropsFromPermissions,
  PermissionEnum,
} from "@modules/account/components";
import { ContextMenu, MenuItem, Icons, Button } from "@components";
import * as accountSelectors from "@modules/account/selectors";
import actionCreators from "@modules/account/actionCreator";
import reportingActionCreators from "@modules/reporting/actionCreator";
import { getCurrentPath } from "@selectors";
import "./header.style.scss";
import { getSystemMessagesReleaseNote } from "@modules/shared/selectors";
import * as reportingSelectors from "@modules/reporting/selectors";
import type { ProfileClient } from "@modules/account/types";
import { TextField } from "@components";
import employeesActionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { getClientContext } from "@modules/context";
import type { Client } from "@modules/context/types";
import * as routes from "@routes/constants";
import KeycloakAdapter from "@src/keycloak";
import { USER_TYPES } from "@modules/account/constants";
import type { ReleaseNote } from "@modules/shared/selectors";
type OwnProps = $Exact<{
  currentPath: string,
  navigate: (event: SyntheticEvent<HTMLElement>) => void,
  toggleSidebar: () => void,
  isCollapsed: boolean,
}>;

type PropsFromState = $Exact<{
  userFirstName: string,
  isAuthenticated: boolean,
  currentPath: string,
  releaseNote: ReleaseNote | null,
  dashboardToday: Array<Object>,
  userClient: ProfileClient,
  clientId: number | null,
  client: Client | null,
  hasContextSwitch: boolean,
  hasReportingContextSwitch: boolean,
  requestReportingDashboardOverview: boolean,
  userType?: string,
  allowEmployeeAudit: boolean,
}>;

type PropsFromDispatch = $Exact<{
  redirect: Function,
  logout: typeof actionCreators.logout,
  getEmployees: typeof employeesActionCreators.SearchEmployeeByName,
  getLoggedUserDashboardOverview: typeof reportingActionCreators.GetLoggedUserDashboardOverview,
  getReleaseNotesDocument: typeof sharedActionCreators.GetReleaseNotesDocument,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromPermissions,
}>;

type StateProps = $Exact<{
  last_name: string,
}>;

class AppHeader extends Component<Props, StateProps> {
  state = {
    last_name: "",
  };

  componentDidMount() {
    if (
      this.props.userClient &&
      this.props.userClient.id &&
      this.props.requestReportingDashboardOverview
    ) {
      this.props.getLoggedUserDashboardOverview({ clientId: this.props.userClient.id });
    }
  }

  downloadReleaseNotes = () => {
    this.props.getReleaseNotesDocument({
      fileName: `${this.props.t("VERSION_CONTROL_NOTES_TITLE")} ${(this.props.releaseNote || {})
        .version || ""}.pdf`,
    });
  };

  redirect = (path: string, filters?: Object) => () => {
    this.props.redirect(path, filters);
  };

  handleSubmit = () => {
    if (this.props.client) {
      typeof this.adminOrReportPage === "string" &&
        this.redirect(
          route("REPORTING_EMPLOYEES_SEARCH_PATH", {
            clientId: (this.props.client || {}).id,
          }),
          {
            lastName: this.state.last_name,
            isActive: true,
          },
        )();
    }
  };

  hasAccessToTasks = () => {
    const res =
      [USER_TYPES.ADMIN, USER_TYPES.SERVICE_MANAGER, USER_TYPES.CLIENT_SUPPLIER].indexOf(
        this.props.userType,
      ) === -1;
    return res;
  };

  handleNewsClick = () => {
    window.open("https://fast.wistia.com/embed/channel/uj1w20q8xu", "_blank");
  };
  handleHelpClick = () => {
    window.open("https://fast.wistia.com/embed/channel/ww9dp1z2r7", "_blank");
  };

  get adminOrReportPage(): string | boolean {
    return (this.props.currentPath.match(/(administration|reporting)/g) || [])[0] || false;
  }

  get showClientSwitch() {
    const { hasContextSwitch, client, currentPath, hasReportingContextSwitch } = this.props;
    const adminPage = currentPath.startsWith(routes.ADMINISTRATION_PATH);
    const reportingPage = currentPath.startsWith(routes.REPORTING_PATH);

    return hasContextSwitch && client && (adminPage || reportingPage);
  }
  navigate = path => () => {
    this.props.navigate(path);
  };

  onChange = e => {
    this.setState({ ...this.state, last_name: e.target.value });
  };
  onKeyUp = e => {
    if (e.keyCode === 13) this.handleSubmit();
  };
  render() {
    const outstandingTasks = (this.props.dashboardToday[2] || {}).value;

    return (
      <View
        name="AppHeader"
        position="sticky"
        top="0px"
        padding={[2, 6]}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        separator="bottom"
        minHeight="67px"
        backgroundColor="white"
        zIndex="1"
      >
        <View alignItems="center" direction="row">
          <Button
            onClick={this.props.toggleSidebar}
            color="secondary"
            materialIcon="menu"
            className="pl-0"
          />
          {this.showClientSwitch && (
            <View>
              {this.adminOrReportPage && (
                <Button
                  color="secondary"
                  size="extra-small"
                  className="p-0 text-color-pumpkin-orange"
                  onClick={this.redirect(route("ADMINISTRATION_CLIENT_PATH"))}
                >
                  {this.props.t("CHANGE_CLIENT")}
                </Button>
              )}
              <div className="text-color-berry-d1 font-size-16 ellipsis">
                {(this.props.client || {}).name || ""}
              </div>
            </View>
          )}
        </View>
        {this.props.isAuthenticated === true && (
          <View direction="row" alignItems="center">
            {this.adminOrReportPage && (
              <View>
                <TextField
                  disabled={this.props.client === null}
                  name="lastName"
                  width="230px"
                  materialIcon={
                    <Button
                      disabled={this.props.client === null}
                      color="secondary"
                      className="position-absolute right-0"
                      materialIcon="search"
                      onClick={this.handleSubmit}
                    />
                  }
                  type="text"
                  className="font-size-14"
                  onChange={this.onChange}
                  onKeyUp={this.onKeyUp}
                  placeholder={this.props.t("SEARCH_EMPLOYEE_SURNAME")}
                />
              </View>
            )}

            {this.props.userType !== USER_TYPES.EMPLOYEE && (
              <View direction="row" alignItems="center">
                {this.hasAccessToTasks() && (
                  <TooltipDropdown
                    titleClassName={!outstandingTasks ? "text-center" : ""}
                    title={this.props.t("TASKS")}
                    description={
                      !!outstandingTasks &&
                      this.props.t("UNFINISHED_TASKS_COUNT", {
                        tasksCnt: outstandingTasks,
                      })
                    }
                    id={"tasks"}
                  >
                    <div
                      className="mx-1 d-flex cursor-pointer align-items-center"
                      onClick={this.redirect(
                        route("REPORTING_TOOLS_TASKS_PATH", {
                          clientId: this.props.userClient.id,
                        }),
                      )}
                    >
                      <div className="d-flex position-relative">
                        {!!outstandingTasks && <span className="red-badge"></span>}
                        <span className="material-icons cursor-pointer fs-34">tasks</span>
                      </div>
                    </div>
                  </TooltipDropdown>
                )}

                {/* News */}
                <TooltipDropdown
                  className="p-2"
                  titleClassName="text-center"
                  title={this.props.t("NEWS")}
                  id={"news"}
                >
                  <div
                    className="mx-1 d-flex cursor-pointer align-items-center mr-4"
                    onClick={this.handleNewsClick}
                  >
                    <span className="material-icons font-size-34 transform-rotate-340 ">
                      campaign
                    </span>
                  </div>
                </TooltipDropdown>

                {/* Help */}
                <TooltipDropdown
                  className="p-2"
                  titleClassName="text-center"
                  title={this.props.t("HELP")}
                  id={"help"}
                >
                  <div
                    className="mx-1 d-flex cursor-pointer align-items-center mr-4"
                    onClick={this.handleHelpClick}
                  >
                    <span className="material-icons font-size-34 ">help</span>
                  </div>
                </TooltipDropdown>
              </View>
            )}
            {/* Profile */}
            <ContextMenu
              mouseOverTriggerEl={<span className="material-icons font-size-34">person</span>}
            >
              <MenuItem
                label={this.props.t("MY_DETAILS")}
                onClick={this.redirect(route("PROFILE_MY_DETAILS"))}
              />
              <MenuItem
                label={this.props.t("MY_EMPLOYEES")}
                hidden={this.props.allowEmployeeAudit === false}
                onClick={this.redirect(route("PROFILE_MY_EMPLOYEES"))}
              />
              <MenuItem
                label={`${this.props.t("VERSION_CONTROL_NOTES_TITLE")} ${(this.props.releaseNote &&
                  this.props.releaseNote.version) ||
                  ""}`}
                onClick={this.downloadReleaseNotes}
              />

              <MenuItem
                label={`${this.props.t("LOGOUT_SIGN")} ${this.props.userFirstName}`}
                onClick={KeycloakAdapter.doLogout}
              />
            </ContextMenu>
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  userFirstName: accountSelectors.getFirstName(state),
  isAuthenticated: accountSelectors.isAuthenticated(state),
  currentPath: getCurrentPath(state),
  releaseNote: getSystemMessagesReleaseNote(state),
  dashboardToday: reportingSelectors.getLoggedUserDashboardToday(state),
  userClient: accountSelectors.getUserClient(state),
  clientId: accountSelectors.getUserClientId(state),
  client: getClientContext(state),
  hasContextSwitch: accountSelectors.hasClientContextSwitch(state),
  hasReportingContextSwitch: accountSelectors.hasReportingClientContextSwitch(state),
  requestReportingDashboardOverview: accountSelectors.accessReportingDashboardOverviewData(state),
  userType: accountSelectors.selectUserType(state),
  allowEmployeeAudit: accountSelectors.getUserAllowEmployeeAudit(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  redirect: push,
  logout: actionCreators.Logout,
  getEmployees: employeesActionCreators.SearchEmployeeByName,
  getReleaseNotesDocument: sharedActionCreators.GetReleaseNotesDocument,
  getLoggedUserDashboardOverview: reportingActionCreators.GetLoggedUserDashboardOverview,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({}),
)(AppHeader);
